import { batch } from 'react-redux';
import template from 'url-template';

import { sensorTypeData } from '../data/sensorTypeData';
import { displayUnitData } from '../data/displayUnitData';

import { get, isEmpty, isNil } from 'lodash';

import { v4 as uuidv4 } from 'uuid';

import {
    RESET_WIZARD,
    SET_ACTIVE_STEP,
    SET_AGENT_NAME,
    SET_AW_LOAD_MESSAGE,
    SET_AW_MESSAGE_MODAL_OPEN,
    SET_AW_PROCESS_IDS,
    SET_AW_TOTAL_SENSORS,
    SET_DASHBOARD_BULK_CREATED,
    SET_EQUIPMENT_CREATED,
    SET_EQUIPMENT_PROCESS_DATA,
    SET_EQUIPMENT_PROCESS_IDS,
    SET_EQUIPMENT_TYPE_NAME,
    SET_PROFILE_NAME,
    SET_SENSOR_PROCESS_DATA,
    SET_SENSOR_PROCESS_IDS,
    SET_SENSORS_CREATED,
    SET_SENSORS_CREATED_EVENT,
    SET_SUMMARIES,
    SET_TARGET_AGENT,
    SET_TARGET_DEVICES,
    SET_TARGET_EQUIPMENT,
    SET_TARGET_EQUIPMENT_CONFIG,
    SET_TARGET_PROFILES,
    SET_TARGET_PROFILE,
    SET_TARGET_PROFILE_OBJECTS,
    SET_TARGET_SENSORS,
    SET_TARGET_SITE,
    SET_WIZARD_COMPLETE,
    SET_SENSORS_ERROR_DETAILS,
    SET_VALIDATE_FORM,
    TOGGLE_LOADING
} from './actionTypes';

import awsHeader from '../helpers/awsAuth';
import k2aBaseApi from '../apis/key2act';

import { handleNetworkError, enqueueSnackbar } from './applicationMessageActions';
import { fetchProfiles } from './profileActions';
import { resetLoadProgress, setLoadProgress, toggleLoadProgressBar } from './uiActions';
import { setBobLoading } from './bobActions';
import { promiseSerial } from '../helpers/promises';

const DEFAULT_DEVICES_LIMIT = 1500;

const BulkEquipmentUrl = template.parse('sites/{siteId}/bulk-equipment');
const BulkSensorsUrl = template.parse('sites/{siteId}/bulk-sensors');
const DevicesUrl = template.parse('/basagents/{agentId}/basdevices{?configurationId,searchFilter,limit}');
const DeviceObjectsUrl = template.parse('/basdevices/{basDeviceId}/basdevice-objects');
const EquipmentUrl = template.parse('/sites/{siteId}/equipment{?limit}');
const ProfileObjectsUrl = template.parse('/basprofiles/{profileId}/basprofile-objects{?limit,page,searchFilter,sort}');

export const addSensor = sensor => async (dispatch, getState) => {
    const sensors = getState().wizard.targetSensors;
    const sensorIndex = sensors.findIndex(item => item.feId === sensor.feId);
    let updatedSensors = [...sensors];

    if (sensorIndex >= 0) {
        updatedSensors[sensorIndex] = sensor;
    } else {
        updatedSensors = [...updatedSensors, sensor];
    }

    dispatch(setTargetSensors(updatedSensors));
};

export const processEquipmentData = (summaryData, newEquipmentCount, existingEquipmentCount, options={}) => async (dispatch, getState) => {
    const header = await awsHeader();
    options.limit || (options.limit = DEFAULT_DEVICES_LIMIT);

    const siteId = getState().wizard.targetSite;
    const devices = getState().wizard.targetDevices;
    const equipmentTypes = getState().equipmentTypes.data;

    const wizardEquipment = [];

    try {
        const response = await k2aBaseApi.get(
            EquipmentUrl.expand({
                siteId,
                ...options
            }),
            header
        );

        const payload = get(response, 'data.data', []);

        payload.forEach(item => {
            devices.forEach(device => {
                if (device.useExisting &&
                    !device.isExcluded &&
                    item.id === device.equipmentId) {
                    item.useExisting = true;
                    wizardEquipment.push(item);
                }
            });
        });

        payload.forEach(item => {
            summaryData.forEach(summary => {
                if (item.id === summary.equipmentId) {
                    item.useExisting = false;
                    wizardEquipment.push(item);
                }
            });
        });

        const message = () => {
            if (newEquipmentCount && !existingEquipmentCount) {
                return `${newEquipmentCount} piece(s) of new equipment prepared successfully.`;
            } else if (!newEquipmentCount && existingEquipmentCount) {
                return `${existingEquipmentCount} piece(s) of existing equipment prepared successfully.`;
            } else {
                return `${newEquipmentCount} piece(s) of new equipment and ${existingEquipmentCount} piece(s) of existing equipment prepared successfully.`;
            }
        };

        if (!wizardEquipment.length) {
            dispatch(enqueueSnackbar('All equipment excluded, no equipment prepared.', 'warning'));
            dispatch({
                type:    TOGGLE_LOADING,
                payload: false
            });
        } else {
            if (summaryData.length) {
                summaryData.forEach(summary => {
                    summary.sensors = [];
                    summary.type = _getEquipmentTypeName(summary.equipmentTypeId, equipmentTypes);
                });
            }

            wizardEquipment.forEach(item => {
                if (item.useExisting) {
                    summaryData.push({
                        equipmentId:     item.id,
                        equipmentTypeId: item.equipmentType.id,
                        messages:        null,
                        name:            item.name,
                        sensors:         [],
                        status:          'Success',
                        type:            item.equipmentType.name
                    });
                }
            });

            batch(() => {
                dispatch(setTargetDevices(devices));
                dispatch(setSummaries(summaryData));
                dispatch(setTargetEquipment(wizardEquipment));
                dispatch(setEquipmentCreated(true));
                dispatch(enqueueSnackbar(message(), 'success'));
                dispatch({
                    type:    TOGGLE_LOADING,
                    payload: false
                });
            });
        }

        dispatch(setMessageModalOpen(false));
    } catch (error) {
        dispatch(setMessageModalOpen(false));
        batch(() => {
            dispatch(enqueueSnackbar('Equipment creation process failed.', 'error'));
            dispatch({
                type:    TOGGLE_LOADING,
                payload: false
            });
        });
    }
};


export const createTargetEquipment = (options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();
    options.limit || (options.limit = DEFAULT_DEVICES_LIMIT);

    dispatch(setLoadMessage('Preparing Equipment for mapping. This may take a few minutes.'));
    dispatch(setMessageModalOpen(true));

    const siteId = getState().wizard.targetSite;
    const devices = getState().wizard.targetDevices;

    let newEquipmentCount = 0;
    let existingEquipmentCount = 0;

    const body = {
        equipment: []
    };
    const deviceObjectPromises = [];

    devices.forEach(device => {
        if (device.isExcluded) return;

        const task = async () => {
            const basDeviceId = device.id;

            const response = await k2aBaseApi.get(
                DeviceObjectsUrl.expand({
                    basDeviceId,
                    ...options
                }),
                header
            );

            device.deviceObjects = get(response, 'data.data', []);
        };

        deviceObjectPromises.push(task);

        if (device.useExisting) {
            existingEquipmentCount++;

            return;
        }

        newEquipmentCount++;

        body.equipment.push({
            name:            device.name,
            equipmentTypeId: device.equipmentType,
            model:           device.modelNumber,
            serialNumber:    device.serialNumber,
            ...device.manufacturer && { equipmentManufacturerId: device.manufacturer }
        });
    });

    await promiseSerial(deviceObjectPromises);

    if (newEquipmentCount === 0) {
        dispatch(processEquipmentData([], newEquipmentCount, existingEquipmentCount));
        return;
    }

    try {
        let summaryData = [];
        let ids = [];

        dispatch(setLoadMessage('Creating Equipment. Please do not refresh your browser.'));

        if (body.equipment.length) {

            const results = await k2aBaseApi.post(
                BulkEquipmentUrl.expand({
                    siteId,
                    ...options
                }),
                body,
                header
            );

            const result = get(results, 'data', []);

            result.forEach(item => {
                ids.push(item.processId);
            });

            dispatch(setEquipmentProcessIds(ids));
        }

        const getProcess = async ids => {
            if (isEmpty(ids)) return;

            const selectedId = ids[0];
            const resolvedData = getState().wizard.equipmentProcessData;
            const selectedData = resolvedData.find(item => item.processId === selectedId);

            const recurseProcesses = async () => {
                return await getProcess(ids);
            };

            if (isNil(selectedData)) {
                setTimeout(recurseProcesses, 1000);
            } else {
                const modifiedIds = ids.filter(id => id !== selectedId);
                summaryData = [...summaryData, ...selectedData.data];
                const recurseModifiedIds = async () => {
                    return await getProcess(modifiedIds);
                };

                if (modifiedIds.length) {
                    setTimeout(recurseModifiedIds, 1000);
                } else {
                    dispatch(processEquipmentData(summaryData, newEquipmentCount, existingEquipmentCount));
                    return;
                }

            }
        };

        await getProcess(ids);
    } catch (error) {
        dispatch(setMessageModalOpen(false));
        batch(() => {
            dispatch(enqueueSnackbar('Equipment creation process failed.', 'error'));
            dispatch({
                type:    TOGGLE_LOADING,
                payload: false
            });
        });
    }
};

export const createTargetSensors = (options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();

    const devices = getState().wizard.targetDevices;
    const equipment = getState().wizard.targetEquipment;
    const profileObjects = getState().wizard.targetProfileObjects;
    const siteId = getState().wizard.targetSite;
    const sensors = getState().wizard.targetSensors;

    const body = {
        sensors: []
    };

    equipment.forEach(item => {
        const equipmentId = get(item, 'id', '');

        sensors.forEach(sensor => {
            body.sensors.push({
                name:               sensor.name,
                sensorTypeId:       sensor.sensorTypeId,
                rawUnitId:          sensor.rawUnitId,
                presentationUnitId: sensor.presentationUnitId,
                equipmentId:        equipmentId,
                systemId:           '',
                deviceObjectId:     _getDeviceObjectId(devices, profileObjects, sensor, equipmentId),
                conversionId:       sensor.conversionId
            });
        });
    });

    let ids = [];

    try {
        let fragments = [];
        let promises = [];
        let sensorList = [...body.sensors];

        while (sensorList.length > 0) {
            fragments.push(sensorList.splice(0, 500));
        }

        fragments.forEach(fragment => {
            const body = {
                sensors: [...fragment]
            };

            promises.push(k2aBaseApi.post(
                BulkSensorsUrl.expand({
                    siteId,
                    ...options
                }),
                body,
                header
            ));
        });

        await Promise.all(promises.map(p => p.catch(err => err))).then(results => {
            results.forEach(item => {
                if (item.data) {
                    item.data.forEach(point => {
                        ids.push(point.processId);
                    });
                }
            });
        });

        dispatch(setTotalSensors(body.sensors.length));
        dispatch(setSensorProcessIds(ids));

        batch(() => {
            dispatch(setSensorsCreated(true));
            dispatch({
                type:    SET_WIZARD_COMPLETE,
                payload: true
            });
        });
    } catch (error) {
        batch(() => {
            dispatch(enqueueSnackbar('Sensor creation process failed.', 'error'));
            dispatch({
                type:    TOGGLE_LOADING,
                payload: false
            });
        });

        return false;
    }
};

export const setBulkSensorCreateComplete = () => async (dispatch, getState) => {
    dispatch({
        type:    SET_DASHBOARD_BULK_CREATED,
        payload: null,
    });
};

export const createBulkSensors = (options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();

    const body = options;
    const siteId = options.siteId;
    let ids = [];

    try {
        dispatch(setBobLoading('bulkSensorCreate'));
        dispatch({
            type:    SET_DASHBOARD_BULK_CREATED,
            payload: Math.floor(Date.now() / 1000),
        });
        
        let promises = [];
        promises.push(k2aBaseApi.post(
            BulkSensorsUrl.expand({
                siteId
            }),
            body,
            header
        ));

        await Promise.all(promises.map(p => p.catch(err => err))).then(results => {
            results.forEach(item => {
                if (item.data) {
                    item.data.forEach(point => {
                        ids.push(point.processId);
                    });
                }
            });
        });

        dispatch(setTotalSensors(body.sensors.length));
        dispatch(setSensorProcessIds(ids));

        batch(() => {
            dispatch(setSensorsCreated(true));
            dispatch({
                type:    SET_SENSORS_CREATED_EVENT,
                payload: Math.floor(Date.now() / 1000)
            });
            dispatch({
                type:    SET_WIZARD_COMPLETE,
                payload: true
            });
            // collectTargetSensors();
        });
    } catch (error) {
        batch(() => {
            dispatch(enqueueSnackbar('Sensor creation process failed.', 'error'));
            dispatch({
                type:    TOGGLE_LOADING,
                payload: false
            });
            dispatch(setBobLoading('bulkSensorCreate'));
        });

        return false;
    }
};

export const collectTargetSensors = (options = {}) => async (dispatch, getState) => {
    const ids = getState().wizard.sensorProcessIds;
    const summaries = getState().wizard.summaries;
    const totalSensors = getState().wizard.totalSensors;

    const sensorLoadProgressBarSettings = {
        currentValue: 0,
        label:        'Sensors Created',
        maxValue:     totalSensors
    };

    dispatch(setLoadProgress(sensorLoadProgressBarSettings));
    dispatch(toggleLoadProgressBar(true));

    summaries.forEach(summary => summary.isLoading = true);

    dispatch(setSummaries(summaries));

    const getProcess = async ids => {
        if (isEmpty(ids)) return;

        try {
            const selectedId = ids[0];
            const resolvedData = getState().wizard.sensorProcessData;
            const selectedData = resolvedData.find(item => item.processId === selectedId);

            const recurseProcesses = async () => {
                return await getProcess(ids);
            };

            if (isNil(selectedData)) {
                setTimeout(recurseProcesses, 1000);
            } else {
                const sensors = [...resolvedData];

                summaries.forEach(summary => {
                    sensors.forEach(sensor => {
                        sensor.data.forEach(item => {
                            if (summary.equipmentId === item.equipmentId) {
                                summary.sensors.push(item);

                                summary.isLoading = false;
                            }
                        });
                    });
                });

                dispatch(setSummaries(summaries));
                dispatch(setLoadProgress(sensorLoadProgressBarSettings));

                const modifiedProcesses = ids.filter(process => process !== selectedId);

                const recurseModifiedProcesses = async () => {
                    return await getProcess(modifiedProcesses);
                };

                if (modifiedProcesses.length) {
                    setTimeout(recurseModifiedProcesses, 1000);
                } else {
                    let createdSensors = 0;
                    let notCreatedSensors = 0;

                    resolvedData.forEach(item => {
                        if (item.processId === selectedId) {
                            item.data.forEach(sensor => {
                                if (sensor.status.toLowerCase() === 'success') {
                                    createdSensors++;
                                } else if (sensor.status.toLowerCase() === 'error') {
                                    notCreatedSensors++;
                                }
                            });
                        }
                    });
                
                    sensorLoadProgressBarSettings.currentValue = createdSensors;

                    dispatch(resetLoadProgress());
                    dispatch(toggleLoadProgressBar(false));
                    dispatch({
                        type:    SET_SENSORS_ERROR_DETAILS,
                        payload: selectedData
                    });
                    if (notCreatedSensors === 0) {
                        dispatch(enqueueSnackbar(`${sensorLoadProgressBarSettings.currentValue} sensor(s) created successfully.`, 'success'));
                    } else if (createdSensors === 0 && notCreatedSensors !== 0) {
                        dispatch(enqueueSnackbar(`${notCreatedSensors} sensor(s) failed to create.`, 'error'));
                    } else {
                        dispatch(enqueueSnackbar(`${sensorLoadProgressBarSettings.currentValue} sensor(s) processed successfully. ${notCreatedSensors} sensor(s) failed to create.`, 'warning'));
                    }
                    dispatch({
                        type:    SET_DASHBOARD_BULK_CREATED,
                        payload: Math.floor(Date.now() / 1000),
                    });
                    dispatch(setBobLoading('bulkSensorCreate'));
                    return true;
                }
            }
        }
        catch (error) {
            dispatch({
                type:    SET_DASHBOARD_BULK_CREATED,
                payload: Math.floor(Date.now() / 1000),
            });
            dispatch(setBobLoading('bulkSensorCreate'));
            return false;
        }
    };

    const success = await getProcess(ids);
    return success;
   
};

export const fetchProfilesByAgent = (agentId, basType, companyId, configurationId, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();
    options.limit || (options.limit = DEFAULT_DEVICES_LIMIT);

    await dispatch(fetchProfiles(companyId));

    const allProfiles = getState().profile.profiles;
    const basTypeProfiles = allProfiles.filter(profile => profile.basType === basType);
    
    try {
        const devicesResponse = await k2aBaseApi.get(
            DevicesUrl.expand({
                agentId,
                configurationId,
                ...options
            }),
            header
        );

        const devicesPayload = get(devicesResponse.data, 'data', []);

        const deviceProfileIds = devicesPayload.map(device => device.basProfileId);

        const payload = basTypeProfiles.filter(profile => deviceProfileIds.includes(profile.id));

        payload.forEach(profile => {
            profile.devices = [];

            devicesPayload.forEach(device => {
                if (profile.id === device.basProfileId) {
                    profile.devices.push(device);
                }
            });
        });

        dispatch(setTargetProfiles(payload));

        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));

        return false;
    }
};

// Set Default Sensor Type and Display Units
export const processTargetProfileObjects = (payload, sensorTypes) => {
    let configuredSensors = [];
    payload.forEach(data => {
        let sensorTypeName = '';
        let sensorTypeId = '';
        let displayUnitName = '';
        let displayUnitId = '';
    
        let type = sensorTypeData.filter(item => item.id === data.name);
        if (!isEmpty(type)) {
            const sensorType = sensorTypes.find(st => st.name === type[0].value);
            sensorTypeId =  sensorType ? sensorType.id : null;
            sensorTypeName = sensorTypeId ? type[0].value : null;
            let unit = displayUnitData.filter(item => item.id === sensorTypeName);
            if (!isEmpty(unit)) {
                displayUnitName = unit[0].value;
                displayUnitId = unit[0].unitId;
            }
    
            if (sensorTypeName) {
                data.isConfigured = true;
            }
        }
            
        data.sensorTypeId = sensorTypeId;
        data.sensorTypeName = sensorTypeName;
        data.displayUnitName = displayUnitName;
        data.displayUnitId = displayUnitId;
    
        if (data.isConfigured) {
            const feId = (data.sensorFrontEndId && !isEmpty(data.sensorFrontEndId)) ? data.sensorFrontEndId : uuidv4();
            const sensorObj = {
                name:                 data.name,
                sensorTypeId:         data.sensorTypeId,
                sensorTypeName:       data.sensorTypeName,
                rawUnitId:            data.displayUnitId,
                rawUnitName:          data.displayUnitName,
                presentationUnitId:   data.displayUnitId,
                presentationUnitName: data.displayUnitName,
                equipmentId:          '',
                systemId:             '',
                deviceObjectId:       '',
                conversionId:         '',
                feId,
                dataType:             data.dataType,
                displayDataType:      data.dataType
            };

            configuredSensors.push(sensorObj);
            data.sensorFrontEndId = feId;
        }
    });
     
    return {
        payload,
        configuredSensors
    };
};

export const fetchProfileObjects = (profileId, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();

    try {
        const profile = getState().wizard.targetProfile;
        const sensorTypes = getState().sensorTypes.data[0].items;

        const getPayload = async (page = 1) => {
            options.limit = DEFAULT_DEVICES_LIMIT;
            options.page = page;

            const response = await k2aBaseApi.get(
                ProfileObjectsUrl.expand({
                    profileId,
                    ...options
                }),
                header
            );

            const payload = get(response, 'data.data', []);
            const totalCount = get(response, 'data.meta.totalCount', 0);

            payload.forEach(item => {
                item.profileName = profile.name;
                item.isConfigured = false;
            });

            const priorPayload = getState().wizard.targetProfileObjects;
            const totalPayload = [...priorPayload, ...payload];

            let processedPayload = processTargetProfileObjects(totalPayload, sensorTypes);

            dispatch(setTargetProfileObjects(processedPayload.payload));
            dispatch(setTargetSensors(processedPayload.configuredSensors));

            page++;

            if (payload.length > 0 && totalPayload.length < totalCount) {
                return await getPayload(page);
            }

            return;
        };

        await getPayload();

        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));

        return false;
    }
};

export const initializeTargetDevices = (options = {}) => async (dispatch, getState) => {
    options.limit || (options.limit = DEFAULT_DEVICES_LIMIT);

    const profile = getState().wizard.targetProfile;
    const devices = getState().wizard.targetProfile.devices;
    const equipmentConfig = getState().wizard.targetEquipmentConfig;

    devices.forEach(device => {
        device.equipmentId = '';
        device.equipmentType = get(equipmentConfig, 'equipmentType.id', '');
        device.manufacturer = get(equipmentConfig, 'manufacturer.id', '');
        device.serialNumber = get(equipmentConfig, 'serialNumber', '');
        device.modelNumber = get(equipmentConfig, 'modelNumber', '');
        device.isExcluded = false;
        device.useExisting = false;
        device.profileName = get(profile, 'name', '');
    });

    dispatch(setTargetDevices(devices));
};

export const initializeTargetSensors = () => async (dispatch, getState) => {
    const sensors = getState().wizard.targetSensors;
    const devices = getState().wizard.targetDevices;
    const equipment = getState().wizard.targetEquipment;
    const profileObjects = getState().wizard.targetProfileObjects;

    if (equipment.length) {
        devices.forEach(device => {
            if (isEmpty(device.equipmentId)) {
                equipment.forEach(item => {
                    if (item.name === device.name) {
                        device.equipmentId = item.id;
                    }
                });
            }
        });
    }

    const updateDevices = devices.filter(device => !device.isExcluded);

    sensors.forEach(sensor => {
        profileObjects.forEach(object => {
            if (object.sensorFrontEndId === sensor.feId) {
                sensor.profileObjectType = object.objectType;
            }
        });
    });

    batch(() => {
        dispatch(setTargetDevices(updateDevices));
        dispatch(setTargetSensors(sensors));
    });
};

export const removeTargetSensor = profileObject => async (dispatch, getState) => {
    const sensors = getState().wizard.targetSensors;
    const profileObjects = getState().wizard.targetProfileObjects;

    const updatedSensors = sensors.filter(sensor => sensor.feId !== profileObject.sensorFrontEndId);

    profileObjects.forEach(object => {
        if (object.sensorFrontEndId === profileObject.sensorFrontEndId) {
            object.sensorFrontEndId = '';
            object.isConfigured = false;
            object.sensorTypeId = '';
            object.displayUnitId = '';
        }
    });

    batch(() => {
        dispatch(setTargetSensors(updatedSensors));
        dispatch(setTargetProfileObjects(profileObjects));
    });
};

export const resetWizard = () => {
    return {
        type: RESET_WIZARD
    };
};

export const resetFromStepOne = () => async (dispatch, getState) => {
    batch(() => {
        dispatch(setEquipmentTypeName(''));
        dispatch(setProfileName(''));
        dispatch(setTargetProfile({}));
        dispatch(setTargetEquipmentConfig({}));
        dispatch(setTargetProfileObjects([]));
        dispatch(setTargetDevices([]));
    });
};

export const resetFromStepTwo = () => async (dispatch, getState) => {
    batch(() => {
        dispatch(setEquipmentTypeName(''));
        dispatch(setTargetEquipmentConfig({}));
        dispatch(setTargetDevices([]));
    });
};

export const setActiveStep = payload => {
    return {
        type: SET_ACTIVE_STEP,
        payload
    };
};

export const setAgentName = payload => {
    return {
        type: SET_AGENT_NAME,
        payload
    };
};

export const setEquipmentCreated = payload => {
    return {
        type: SET_EQUIPMENT_CREATED,
        payload
    };
};

export const setEquipmentTypeName = payload => {
    return {
        type: SET_EQUIPMENT_TYPE_NAME,
        payload
    };
};

export const setLoadMessage = payload => {
    return {
        type: SET_AW_LOAD_MESSAGE,
        payload
    };
};

export const setMessageModalOpen = payload => {
    return {
        type: SET_AW_MESSAGE_MODAL_OPEN,
        payload
    };
};

export const setProcessIds = payload => {
    return {
        type: SET_AW_PROCESS_IDS,
        payload
    };
};

export const setEquipmentProcessIds = payload => {
    return {
        type: SET_EQUIPMENT_PROCESS_IDS,
        payload
    };
};

export const setEquipmentProcessData = payload => {
    return {
        type: SET_EQUIPMENT_PROCESS_DATA,
        payload
    };
};

export const setSensorProcessIds = payload => {
    return {
        type: SET_SENSOR_PROCESS_IDS,
        payload
    };
};

export const setSensorProcessData = payload => {
    return {
        type: SET_SENSOR_PROCESS_DATA,
        payload
    };
};

export const setProfileName = payload => {
    return {
        type: SET_PROFILE_NAME,
        payload
    };
};

export const setSensorsCreated = payload => {
    return {
        type: SET_SENSORS_CREATED,
        payload
    };
};

export const setSummaries = payload => {
    return {
        type: SET_SUMMARIES,
        payload
    };
};

export const setTargetAgent = payload => {
    return {
        type: SET_TARGET_AGENT,
        payload
    };
};

export const setTargetDevices = payload => {
    return {
        type: SET_TARGET_DEVICES,
        payload
    };
};

export const setTargetEquipment = payload => {
    return {
        type: SET_TARGET_EQUIPMENT,
        payload
    };
};

export const setTargetEquipmentConfig = payload => {
    return {
        type: SET_TARGET_EQUIPMENT_CONFIG,
        payload
    };
};

export const setTargetProfile = payload => {
    return {
        type: SET_TARGET_PROFILE,
        payload
    };
};

export const setTargetProfiles = payload => {
    return {
        type: SET_TARGET_PROFILES,
        payload
    };
};

export const setTargetProfileObjects = payload => {
    return {
        type: SET_TARGET_PROFILE_OBJECTS,
        payload
    };
};

export const setTargetProfileObject = payload => async (dispatch, getState) => {
    const profileObjects = getState().wizard.targetProfileObjects;

    profileObjects.forEach(object => {
        if (object.id === payload.id) {
            object = {...object, ...payload};
        }
    });

    dispatch(setTargetProfileObjects(profileObjects));
};

export const setTargetSensors = payload => {
    return {
        type: SET_TARGET_SENSORS,
        payload
    };
};

export const setTargetSite = payload => async (dispatch, getState) => {
    const currentSite = getState().wizard.targetSite;

    if (payload !== currentSite) {
        dispatch(resetWizard());
    }

    dispatch({
        type: SET_TARGET_SITE,
        payload
    });
};

export const setTotalSensors = payload => {
    return {
        type: SET_AW_TOTAL_SENSORS,
        payload
    };
};

export const setValidateForm = payload => {
    return {
        type: SET_VALIDATE_FORM,
        payload
    };
};

export const updateTargetDevices = device => async (dispatch, getState) => {
    const devices = getState().wizard.targetDevices;

    devices.forEach(targetDevice => {
        if (targetDevice.id === device.id) {
            targetDevice = {...targetDevice, ...device};
        }
    });

    dispatch(setTargetDevices(devices));
};

const _getEquipmentTypeName = (equipmentTypeId, equipmentTypes) => {
    return equipmentTypes.find(type => type.id === equipmentTypeId).name;
};

const _getDeviceObjectId = (devices, profileObjects, sensor, equipmentId) => {
    let deviceId;
    let object;

    profileObjects.forEach(item => {
        if (item.sensorFrontEndId === sensor.feId) {
            object = {...item};
        }
    });

    devices.forEach(device => {
        device.deviceObjects.forEach(item => {
            if (item.profileObjectId === object.id &&
                device.equipmentId === equipmentId) {
                deviceId = get(item, 'id', '');
            }
        });
    });

    return deviceId;
};
